import React, { useCallback, useMemo, useEffect, useState } from 'react';
import _ from 'lodash';
import { useInput, BasicSelect, OptionType } from '@tradingblock/components';
import { CashieringSearchEntityType, TransferStates, TransferState } from '@tradingblock/types';
import { useCashieringActions, useCashieringData } from '../useCashiering';
import { Timeframe } from '../../../../components/basic/Timeframe';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { isFetchingCashieringSearchEntities } from '../../../../data/global/selectors/admin/cashieringSelectors';

// prettier-ignore
export const typeOptions: OptionType<CashieringSearchEntityType | undefined>[] = [
  { value: 'All', label: 'Type: All', data: undefined },
  { value: 'ACH Relationship', label: 'ACH Relationship', data: CashieringSearchEntityType.AchRelationship },
  { value: 'Transfer', label: 'Transfer', data: CashieringSearchEntityType.Transfer, },
  { value: 'Transfer Instructions', label: 'Transfer Instructions', data: CashieringSearchEntityType.TransferWireInstruction },
];

// add a space between capitalized words
export const statusOptions: OptionType<TransferState | undefined>[] = _(TransferStates)
  .map((s: TransferState): OptionType<TransferState | undefined> => ({ value: s, label: s, data: s }))
  .union([{ value: 'All', label: 'Status: All', data: undefined }])
  .value()
  .map(o => ({ ...o, label: o.label.replace(/([a-z])([A-Z])/g, '$1 $2') }));

// Place 'All' option at the top of the list
const allOption = statusOptions.find(o => o.value === 'All');
if (allOption) {
  statusOptions.splice(statusOptions.indexOf(allOption), 1);
  statusOptions.unshift(allOption);
}

type CashieringFiltersProps = {
  handleSetSelectedRow: (index: number | undefined) => void;
};

export const CashieringFilters: React.FC<CashieringFiltersProps> = ({ handleSetSelectedRow }) => {
  const { search, timeframe, types, status } = useCashieringData();
  const { search: onSearch, setTimeframe, setType, setStatus } = useCashieringActions();

  const [searchSubmitted, setSearchSubmitted] = useState(false);

  const onSearchSubmit = useCallback(
    (search: string) => {
      onSearch(search.trim());
      setSearchSubmitted(!_.isEmpty(search.trim()));
    },
    [search]
  );

  const [searchValue, setSearchValue, inputProps] = useInput(undefined, { onEnter: onSearchSubmit });

  const onClearSearch = useCallback(() => {
    onSearchSubmit('');
    setSearchValue('');
    sessionStorage.removeItem('cashieringSearch');
    handleSetSelectedRow(undefined);
  }, []);

  useEffect(() => {
    // if there is a search value but the icon for clearing is missing... (using the clear button's inverted logic)
    if (searchValue && (!searchSubmitted && !currentSearch)) {
      // force a search
      setSearchValue(searchValue.trim());
    }
  }, [searchSubmitted]);

  useEffect(() => {
    // if search is present but there is no searchValue update the searchValue
    if (search && _.isUndefined(searchValue)) {
      setSearchValue(search);
    }
    // if search is present and searchValue is an empty string update the searchValue
    if (search && searchValue === '') {
      setSearchValue(search);
    }
  }, [search]);

  const currentSearch = useMemo(() => {
    if (!_.isUndefined(searchValue)) {
      return searchValue;
    }
    return search || '';
  }, [search, searchValue]);

  const currentType = useMemo(() => {
    const firstType = _.first(types);
    if (firstType) {
      return firstType;
    }
    const defaultType = _.first(typeOptions);
    return defaultType ? defaultType.label : '';
  }, [
    types,
    typeOptions,
    currentSearch,
    searchSubmitted,
    isFetchingCashieringSearchEntities,
    searchValue,
    search,
    searchSubmitted,
  ]);

  const currentStatus = useMemo(() => {
    const firstStatus = _.first(status);
    if (firstStatus) {
      return firstStatus;
    }
    const defaultStatus = _.first(statusOptions);
    return defaultStatus ? defaultStatus.label : '';
  }, [
    status,
    statusOptions,
    currentSearch,
    searchSubmitted,
    isFetchingCashieringSearchEntities,
    searchValue,
    search,
    searchSubmitted,
  ]);

  const onTimeframeChange = useCallback((startDate: Date | undefined, endDate: Date | undefined) => {
    if (_.isNil(startDate)) {
      setTimeframe(undefined, undefined);
    } else if (_.isNil(endDate)) {
      setTimeframe(startDate, undefined);
    } else {
      setTimeframe(startDate, endDate);
    }
  }, []);
  const onTypeChange = useCallback((val: OptionType | OptionType[] | null | undefined, { action }) => {
    const types = _.isNil(val) ? [] : _.isArray(val) ? val : [val];
    setType(types.filter(v => v.data !== undefined).map(t => t.data));
    sessionStorage.removeItem('cashieringSearch');
    handleSetSelectedRow(undefined);
  }, []);
  const onStatusChange = useCallback((val: OptionType | OptionType[] | null | undefined, { action }) => {
    const values = _.isNil(val) ? [] : _.isArray(val) ? val : [val];
    setStatus(values.filter(v => v.data !== undefined).map(t => t.data));
    sessionStorage.removeItem('cashieringSearch');
    handleSetSelectedRow(undefined);
  }, []);

  return (
    <div className="table-toolbar table-toolbar-top">
      <div className="table-toolbar-primary">
        <input
          className="input input-dark input-tall"
          type="text"
          placeholder={'Search...'}
          value={currentSearch}
          {...inputProps}
        />
        {currentSearch && (
          <i
            className="fa fa-times fa-2x"
            onClick={onClearSearch}
            style={{ color: 'var(--blue)', cursor: 'pointer', paddingLeft: '0.32em' }}
          />
        )}
      </div>
      <div className="table-toolbar-secondary">
        <Timeframe
          initialStartDate={timeframe.startDate}
          initialEndDate={timeframe.endDate}
          btnClassName="btn-dark btn-tall dropdown-toggle text-left"
          placeholderClassName="select__placeholder"
          caret={<>&nbsp;&nbsp;</>}
          onSave={onTimeframeChange}
        />
        <BasicSelect
          onChange={onTypeChange}
          placeholder={currentType}
          className="btn btn-dark btn-tall dropdown-toggle text-left"
          options={typeOptions}
          isClearable={false}
          value={currentType}
        />
        <BasicSelect
          onChange={onStatusChange}
          placeholder={currentStatus}
          className="btn btn-dark btn-tall dropdown-toggle text-left"
          options={statusOptions}
          isClearable={false}
          value={currentStatus}
        />
      </div>
    </div>
  );
};
